import { ColorSchemeScript, MantineProvider as MantineCoreProvider } from "@mantine/core";
import { type ReactNode } from "react";

import { theme } from "../_vendor/theme";

import { ModalsHost } from "./modals";
import { NotificationsHost } from "./notifications";

export interface ShelfCycleProviderProps {
  children: ReactNode;
}

/**
 * Mounts the theme provider plus the two imperative-API hosts (SC-2700): the modals provider
 * that `openConfirmModal` renders through, and the notifications portal that `showNotification`
 * renders through. Both previously sat in `_app.tsx` outside the boundary; every consumer of the
 * imperative functions renders below this provider in both `appProvider` branches.
 *
 * Mantine 7 ships static stylesheets instead of runtime Emotion styles, so this seam owns their
 * imports: core first, then each other installed package (SC-2703), in `../_vendor/theme` — the
 * first module this file imports. Reached through the `~/ui` barrel that `_app.tsx` loads before
 * `globals.css`, they precede the application's own CSS in the bundle — the order Mantine 6's
 * prepended Emotion cache gave its styles. Every
 * scheme-dependent Mantine variable is scoped to `:root[data-mantine-color-scheme]`, which the
 * provider only sets after hydration; the inline script sets it before the first painted
 * Mantine element, so server-rendered markup resolves its colors on first paint.
 */
export const ShelfCycleProvider = ({ children }: ShelfCycleProviderProps): JSX.Element => {
  return (
    <MantineCoreProvider theme={theme} forceColorScheme="light">
      <ColorSchemeScript forceColorScheme="light" />
      <ModalsHost>
        <NotificationsHost />
        {children}
      </ModalsHost>
    </MantineCoreProvider>
  );
};
